import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import LoadingContext from '../../../../_metronic/layout/core/Loading'
import { useMainContext } from '../../../../context'
import { useAuth } from '../../../modules/auth'
import { handleGetRequest } from '../../../services'
import { IRecruiter } from '../CampaignTable'

interface SupportPlayer {
  _id: string
  first_name?: string
  last_name?: string
  email?: string
}

interface Option {
  value: string
  label: string
}

interface CampaignAssignSupporterModalProps {
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  recruiters: IRecruiter[]
  assignedUserIds: string[]
  submitCallback: (values: {
    user_id: string
    short_user_id: string
    additional: boolean
  }) => void | Promise<void>
}

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: '44px',
    borderRadius: '0.85rem',
    border: '1px solid #e4e6ef',
    backgroundColor: '#ffffff',
  }),
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
}

const CampaignAssignSupporterModal: React.FC<CampaignAssignSupporterModalProps> = ({
  showModal,
  setShowModal,
  recruiters,
  assignedUserIds,
  submitCallback,
}) => {
  const { currentUser } = useAuth()
  const { companyofPortal } = useMainContext()
  const { setLoading } = useContext(LoadingContext)
  const [supporters, setSupporters] = useState<SupportPlayer[]>([])
  const [selectedSupporter, setSelectedSupporter] = useState<Option | null>(null)
  const [selectedRecruiter, setSelectedRecruiter] = useState<Option | null>(null)
  // Supporter already in the campaign who also supports another participant (e.g. several kids)
  const [additional, setAdditional] = useState(false)

  useEffect(() => {
    if (!showModal) {
      setSelectedSupporter(null)
      setSelectedRecruiter(null)
      setAdditional(false)
      return
    }
    const getSupporters = async () => {
      const sport_id = companyofPortal?.company_id
        ? [companyofPortal?.company_id]
        : currentUser?.user?.sports_list?.map((item: any) => item._id)
      const params = {
        organisation_id: currentUser?.user?.organisation_id?._id,
        sport_id,
        only_consented_users: false,
        only_active: true,
        organisation_has_enabled_consent:
          !!currentUser?.user?.organisation_id?.user_consent_sharing_enabled,
        limit: 9999,
        page: 1,
      }
      const res = await handleGetRequest(`/goal_support/support_players`, { params })(setLoading)
      setSupporters(res?.data || [])
    }
    getSupporters()
  }, [showModal])

  const supporterOptions = useMemo<Option[]>(() => {
    const assigned = new Set(assignedUserIds)
    return supporters
      .filter((s) => additional === assigned.has(s._id))
      .map((s) => {
        const name = `${s.first_name || ''} ${s.last_name || ''}`.trim()
        const label = [name || 'Ukjent navn', s.email].filter(Boolean).join(' — ')
        return { value: s._id, label }
      })
  }, [supporters, assignedUserIds, additional])

  const recruiterOptions = useMemo<Option[]>(
    () =>
      recruiters.map((r) => ({
        value: r.short_user_id,
        label: `${r.name || 'Ukjent'} (${r.short_user_id})`,
      })),
    [recruiters]
  )

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' keyboard={false}>
      <div className='modal-body'>
        <h4 className='mb-2'>Legg til støttespiller</h4>
        <p className='text-muted fs-7'>
          Velg en støttespiller med aktiv støtte som ikke allerede er i kampanjen, og hvilken
          deltager den skal tilknyttes.
        </p>

        <div className='form-check mt-7'>
          <input
            className='form-check-input'
            type='checkbox'
            id='assignSupporterAdditional'
            checked={additional}
            onChange={(e) => {
              setAdditional(e.target.checked)
              setSelectedSupporter(null)
            }}
          />
          <label className='form-check-label' htmlFor='assignSupporterAdditional'>
            Støttespilleren er allerede i kampanjen og støtter flere deltagere (f.eks. flere barn)
          </label>
        </div>

        <div className='row mb-3 mt-7'>
          <label className='col-lg-3 col-form-label fw-bold fs-6'>Støttespiller:</label>
          <div className='col-lg-9 fv-row'>
            <Select
              styles={selectStyles}
              options={supporterOptions}
              value={selectedSupporter}
              onChange={(opt) => setSelectedSupporter(opt as Option)}
              placeholder='Velg støttespiller...'
              noOptionsMessage={() => 'Ingen tilgjengelige støttespillere'}
              isClearable
            />
          </div>
        </div>

        <div className='row mb-3 mt-7'>
          <label className='col-lg-3 col-form-label fw-bold fs-6'>Deltager:</label>
          <div className='col-lg-9 fv-row'>
            <Select
              styles={selectStyles}
              options={recruiterOptions}
              value={selectedRecruiter}
              onChange={(opt) => setSelectedRecruiter(opt as Option)}
              placeholder='Velg deltager...'
              noOptionsMessage={() => 'Ingen deltagere i kampanjen'}
              isClearable
            />
          </div>
        </div>

        <br />
        <button
          type='button'
          style={{ float: 'right' }}
          disabled={!selectedSupporter || !selectedRecruiter}
          onClick={async () => {
            if (!selectedSupporter || !selectedRecruiter) return
            await submitCallback({
              user_id: selectedSupporter.value,
              short_user_id: selectedRecruiter.value,
              additional,
            })
          }}
          className='btn btn-primary'
        >
          Lagre
        </button>
      </div>
    </Modal>
  )
}

export default React.memo(CampaignAssignSupporterModal)
